import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CreditCard, Crown, Download, FilePdf, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Card } from "./shared";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

export default function PianoSection() {
  const [plan, setPlan] = useState(null);
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [sel, setSel] = useState({ plan_id: "", interval: "monthly" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/billing/my-plan").then((r) => setPlan(r.data)).catch(() => {});
    api.get("/billing/my-payments").then((r) => setPayments(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (plan && !plan.comp && plan.subscription_status !== "attiva") {
      api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
    }
  }, [plan]);

  if (!plan) return <div className="text-sm text-zinc-400 py-8 text-center" data-testid="piano-loading">Caricamento...</div>;

  const downloadPdf = async (p) => {
    try {
      const r = await api.get(`/billing/my-payments/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.invoice || "ricevuta"}-dishenda.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadStripe = async (p) => {
    try {
      const r = await api.get(`/billing/my-payments/${p.id}/stripe-invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.invoice || "fattura"}-stripe.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Fattura Stripe non disponibile per questo pagamento"); }
  };

  const subscribe = async () => {
    if (!sel.plan_id) { toast.error("Scegli un piano"); return; }
    setBusy(true);
    try {
      const r = await api.post("/billing/subscribe", sel);
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const allOpen = plan.features === null;
  const statusLabel = plan.comp ? "Account omaggio"
    : plan.subscription_status === "attiva" ? "Abbonamento attivo"
    : "Prova gratuita — tutto sbloccato";

  return (
    <div className="space-y-4" data-testid="piano-section">
      <Card title="Il tuo piano" desc="Stato dell'abbonamento e moduli attivi sul tuo account" testid="piano-card">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-xl bg-zinc-900 flex items-center justify-center shrink-0">
            <Crown size={24} weight="duotone" className="text-cyan-300" />
          </span>
          <div>
            <p className="font-display text-2xl font-extrabold text-zinc-900" data-testid="piano-nome">
              {plan.plan_name || (plan.comp ? "Omaggio" : "Prova gratuita")}
            </p>
            <p className="text-xs text-zinc-500" data-testid="piano-stato">
              {statusLabel}{!plan.comp && plan.prezzo_mensile ? ` · €${plan.prezzo_mensile}/mese o €${plan.prezzo_annuale}/anno` : ""}
            </p>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Moduli</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {(plan.all_features || []).map((k) => {
              const unlocked = allOpen || plan.features.includes(k);
              const inner = (
                <>
                  {unlocked
                    ? <Check size={15} weight="bold" className="text-emerald-600 shrink-0" />
                    : <Lock size={14} weight="bold" className="text-zinc-400 shrink-0" />}
                  <span className={unlocked ? "text-zinc-800" : "text-zinc-400"}>{plan.feature_labels[k] || k}</span>
                  {!unlocked && <span className="ml-auto text-[10px] font-bold text-cyan-700">UPGRADE</span>}
                </>
              );
              return unlocked ? (
                <div key={k} className="flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-zinc-50 border border-zinc-200" data-testid={`piano-mod-${k}`}>
                  {inner}
                </div>
              ) : (
                <Link key={k} to={`/upgrade?feature=${k}`} data-testid={`piano-mod-${k}`}
                  className="flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-zinc-50 border border-dashed border-zinc-300 hover:border-cyan-400 transition-colors">
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>

        {!allOpen && (
          <a href="mailto:info@cavadaconsulting.it?subject=Richiesta%20upgrade%20piano"
            data-testid="piano-upgrade-cta"
            className="inline-flex h-11 px-6 rounded-md bg-zinc-900 text-white text-sm font-semibold items-center hover:bg-zinc-800 transition-colors">
            Richiedi upgrade o cambio piano
          </a>
        )}
        {allOpen && !plan.comp && (
          <p className="text-xs text-zinc-500" data-testid="piano-trial-note">
            Durante la prova hai accesso a tutti i moduli. Alla scadenza scegli il piano e mantieni ciò che ti serve.
          </p>
        )}
      </Card>

      {!plan.comp && plan.subscription_status !== "attiva" && plans.length > 0 && (
        <Card title="Attiva l'abbonamento" desc="Pagamento sicuro su Stripe — rinnovo automatico, disdici quando vuoi" testid="piano-subscribe">
          <div className="flex gap-2" data-testid="piano-interval">
            {[["monthly", "Mensile"], ["yearly", "Annuale · 2 mesi gratis"]].map(([v, l]) => (
              <button key={v} onClick={() => setSel((s) => ({ ...s, interval: v }))} data-testid={`piano-interval-${v}`}
                className={`h-9 px-4 rounded-md text-xs font-bold transition-colors ${sel.interval === v ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            {plans.map((p) => {
              const price = sel.interval === "yearly" ? p.prezzo_annuale : p.prezzo_mensile;
              const active = sel.plan_id === p.id;
              return (
                <button key={p.id} disabled={!price} onClick={() => setSel((s) => ({ ...s, plan_id: p.id }))}
                  data-testid={`piano-plan-${p.id}`}
                  className={`text-left rounded-lg border p-3 transition-colors disabled:opacity-40 ${active ? "border-cyan-500 bg-cyan-50 ring-1 ring-cyan-500" : "border-zinc-200 hover:border-zinc-400"}`}>
                  <p className="font-bold text-sm text-zinc-900">{p.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {price ? <>€{price}<span className="text-zinc-400">/{sel.interval === "yearly" ? "anno" : "mese"}</span></> : "Prezzo non disponibile"}
                  </p>
                </button>
              );
            })}
          </div>
          <button onClick={subscribe} disabled={busy || !sel.plan_id} data-testid="piano-subscribe-btn"
            className="inline-flex h-11 px-6 rounded-md bg-zinc-900 text-white text-sm font-semibold items-center gap-2 hover:bg-zinc-800 transition-colors disabled:opacity-50">
            <CreditCard size={17} weight="bold" />
            {busy ? "Reindirizzamento a Stripe…" : "Attiva con Stripe"}
          </button>
        </Card>
      )}

      <Card title="Rinnovo e storico pagamenti" desc="Date dell'abbonamento e fatture Dishenda" testid="piano-storico">
        {plan.trial_ends_at && plan.subscription_status !== "attiva" && (
          <p className="text-sm text-zinc-700 bg-cyan-50 border border-cyan-200 rounded-md px-3 py-2" data-testid="piano-trial-end">
            Prova gratuita attiva fino al <strong>{fmtDate(plan.trial_ends_at)}</strong>
          </p>
        )}
        {plan.subscription_status === "attiva" && (
          <p className="text-sm text-zinc-700" data-testid="piano-attivo-info">
            Abbonamento attivo{plan.activated_at ? <> dal <strong>{fmtDate(plan.activated_at)}</strong></> : ""}.
            Il rinnovo è automatico alla stessa data ogni mese/anno secondo il piano scelto.
          </p>
        )}
        {payments.length === 0 ? (
          <p className="text-sm text-zinc-400" data-testid="piano-no-payments">Nessun pagamento registrato.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="piano-payments">
            <table className="w-full text-sm min-w-[420px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                  <th className="py-2 pr-3">Data</th><th className="py-2 pr-3">Fattura</th>
                  <th className="py-2 pr-3">Metodo</th><th className="py-2 pr-3">Importo</th><th className="py-2">Stato</th><th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-50 last:border-0" data-testid={`pay-row-${p.id}`}>
                    <td className="py-2 pr-3 text-zinc-600">{fmtDate(p.created_at)}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-zinc-500">{p.invoice || "—"}</td>
                    <td className="py-2 pr-3 text-zinc-600 capitalize">{p.method || "—"}</td>
                    <td className="py-2 pr-3 font-semibold text-zinc-900">€{p.amount}</td>
                    <td className="py-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        p.status === "pagato" ? "bg-emerald-100 text-emerald-700"
                        : p.status === "fallito" ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-700"}`}
                        data-testid={`pay-status-${p.id}`}>{p.status}</span>
                      {p.error && <span className="block text-[10px] text-rose-500 mt-0.5">{p.error}</span>}
                    </td>
                    <td className="py-2 pl-2 text-right">
                      <div className="flex justify-end gap-1">
                        {p.stripe_invoice_id && !p.mock && (
                          <button onClick={() => downloadStripe(p)} title="Fattura fiscale ufficiale Stripe" data-testid={`pay-stripe-${p.id}`}
                            className="p-1.5 rounded-md border border-cyan-200 bg-cyan-50 text-cyan-700 hover:border-cyan-400 transition-colors">
                            <FilePdf size={15} weight="bold" />
                          </button>
                        )}
                        <button onClick={() => downloadPdf(p)} title="Scarica ricevuta PDF" data-testid={`pay-pdf-${p.id}`}
                          className="p-1.5 rounded-md border border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:border-zinc-400 transition-colors">
                          <Download size={15} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
